import { ChangeDetectionStrategy, Component, HostBinding, computed } from '@angular/core';

import { SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';

import { VisualizationBetState, VisualizationConstants, betStateMapping } from '../../models/my-bets-visualization-types';
import { MyBetsVisualizationService } from '../../services/my-bets-visualization.service';
import { VisualizationBaseComponent } from './visualization-base.component';

@Component({
    standalone: true,
    selector: 'ms-my-bets-textual-visualization',
    templateUrl: 'my-bets-textual-visualization.component.html',
    styleUrl: './my-bets-textual-visualization.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBetsTextualVisualizationComponent extends VisualizationBaseComponent {
    @HostBinding('class') get className() {
        return this.betStateClassName() + ' live-bets-tracking ';
    }

    stats = computed(() => {
        const currStatValue = this.currentStatValue() > 0 ? Math.abs(this.statsData().player1CurrStat - this.statsData().player2CurrStat) : 0;
        const metaDataInfo = this.getMetaData();

        return { currStat: currStatValue, metaData: metaDataInfo };
    });

    private betStateClassName = computed(() =>
        this.myBetsVisualizationService.getBetStateClassName(this.betMarketInfo().state, this.currentStatValue(), this.isCashedOut()),
    );

    constructor(
        private sitecoreMyBets: SitecoreMyBetsConfig,
        private myBetsVisualizationService: MyBetsVisualizationService,
    ) {
        super();
    }

    private getCurrentStat(): { statValue: number; playerName: string } {
        const statsData = this.statsData();
        const marketStats = this.myBetsVisualizationService.marketStatsDetails(this.event(), this.betMarketInfo().marketId);
        let playerName = this.betMarketInfo().optionName;

        if (marketStats.statsType !== VisualizationConstants.Team && this.event().playerStats) {
            return { statValue: statsData.player1CurrStat - statsData.player2CurrStat, playerName };
        } else {
            const { participant1, participant2 } = this.myBetsVisualizationService.getParticipants(this.event().participants);
            switch (statsData.player.playerName) {
                case VisualizationConstants.HomePlayer:
                    return { statValue: statsData.player1CurrStat - statsData.player2CurrStat, playerName: participant1 };
                case VisualizationConstants.AwayPlayer:
                    return { statValue: statsData.player2CurrStat - statsData.player1CurrStat, playerName: participant2 };
                default:
                    return { statValue: 0, playerName };
            }
        }
    }

    private getMetaData(): string {
        const currentStat = this.getCurrentStat();
        return this.getMetaDataBasedOnBetState(currentStat.statValue, currentStat.playerName);
    }

    private getMetaDataBasedOnBetState(calCurrentStat: number, player: string): string {
        const visualizationState = betStateMapping[this.betMarketInfo().state];
        const { TiedAt, AheadBy, BehindBy, WonBy, LostBy } = this.sitecoreMyBets.myBets;
        const statCheck = this.currentStatValue() === 0 || calCurrentStat === 0;

        switch (visualizationState) {
            case VisualizationBetState.Open:
            case VisualizationBetState.Cancelled:
                if (statCheck) {
                    return `${player} ${TiedAt}`;
                }

                return `${player} ${calCurrentStat > 0 ? AheadBy : BehindBy}`;
            case VisualizationBetState.Won:
                return `${player} ${WonBy}`;
            case VisualizationBetState.Lost:
                return `${player} ${LostBy}`;
            default:
                return '';
        }
    }
}
